import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useProductName } from '../../../lib/use-brand';
import { logDownloadApp } from '../../../lib/appsflyer';
import { ANDROID_URL, IOS_URL } from '../../../lib/app-store-links';
import { useInstallQrLink } from '../../../lib/use-install-qr-link';
import { Card, Typography } from '../ui';
import styles from './MobileAppCard.module.css';

// "Download Symphony mobile app" — a scan target plus both store badges, offered ONLY to an account
// whose owner has never had the app on a phone, and only when the server CONFIRMS it (see
// `showMobileAppCard`; an unknown hides it).
//
// The redesign (Figma "Catch Up states" 7419:47452) moved it UP into the home header, beside the brief
// card, where it sits BARE on the page — `bare` drops the Card surface for that placement. The mobile
// home keeps it as a `Card` (`bare={false}`, the default), matching its own frame.
//
// No native counterpart, deliberately: the card exists to get the app onto a phone, and inside the app
// there is nothing left to ask for. That is the written-down exception (see orion-web/CLAUDE.md).

const PAGE = 'home';

/** The Figma leaf is 145×145; the QR is generated, not an exported image, so it renders a real code. */
const QR_SIZE = 145;

export function MobileAppCard({ bare = false }: { bare?: boolean } = {}) {
  const { t } = useTranslation();
  const productName = useProductName();
  // The store badges keep the plain install link: they are tapped on the desktop the card is being read
  // on, where they open a store web page and install nothing. The QR is the actual hand-off to a phone,
  // so it is the one that has to carry an invite (see `useInstallQrLink`).
  const qrLink = useInstallQrLink();

  const inner = (
    <div className={styles.row}>
      <div className={styles.copy}>
        <div className={styles.text}>
          <Typography variant="p2" color="primary" component="p" className={styles.caption}>
            {t('home.mobileApp.caption')}
          </Typography>
          <Typography variant="h3" color="strong" component="h2" className={styles.title}>
            {t('home.mobileApp.title', { productName })}
          </Typography>
        </div>
        <StoreBadges />
      </div>
      {/* The QR sits on a white, rounded, padded panel (design QA 12760332115, Figma 13528-98518): a
          DS sunken Card supplies the white surface + radius; `.qrPanel` adds the equal quiet-zone
          padding around the code. */}
      <div className={styles.qr}>
        <Card variant="sunken" size="sm" disablePadding className={styles.qrPanel}>
          {/* Black-on-white in BOTH themes — a QR is a machine target, and an inverted one is
              refused by a meaningful share of scanners. Its own white field is the plate. */}
          <QRCodeSVG
            value={qrLink}
            size={QR_SIZE}
            level="M"
            marginSize={0}
            bgColor="var(--ds-white)"
            fgColor="var(--ds-black)"
            title={t('home.mobileApp.qrAlt', { productName })}
          />
        </Card>
      </div>
    </div>
  );

  if (bare) return <div className={styles.bare}>{inner}</div>;
  return <Card className={styles.card}>{inner}</Card>;
}

// The official store assets, each linking to its own store (Apple → App Store, Google → Play, per both
// vendors' brand guidelines). Both are edge-to-edge so equal height renders equal-size buttons: Apple's
// SVG is already tight (119.66×40); Google's official PNG bakes in ~36% transparent clear space (its
// pill is only 561×160 of 646×250), so we use a trimmed copy — otherwise at the same box height its
// visible button would sit noticeably shorter than Apple's. (get-app.tsx keeps the padded asset, which
// its own layout sizes around.)
function StoreBadges() {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  return (
    <div className={styles.badges}>
      <a
        href={IOS_URL}
        target="_blank"
        rel="noreferrer"
        className={styles.badgeLink}
        onClick={() => {
          logPageClickBi({ page_name: PAGE, button_name: 'app_store' }, { componentName: 'download-app-ios' });
          logDownloadApp('ios');
        }}
      >
        <img src={`${basePath}/app-store-badge.svg`} alt={t('getApp.badgeAppStoreAlt')} height={30} width={90} />
      </a>
      <a
        href={ANDROID_URL}
        target="_blank"
        rel="noreferrer"
        className={styles.badgeLink}
        onClick={() => {
          logPageClickBi({ page_name: PAGE, button_name: 'google_play' }, { componentName: 'download-app-android' });
          logDownloadApp('android');
        }}
      >
        <img src={`${basePath}/google-play-badge-trimmed.png`} alt={t('getApp.badgeGooglePlayAlt')} height={30} width={105} />
      </a>
    </div>
  );
}
